import React, { useState } from 'react';
import {
    IconButton,
    Tooltip,
    Popover,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Snackbar
} from '@mui/material';
import { BugReport, Close } from '@mui/icons-material';
import { vortxColors, vortxTypography, useThemeMode } from '@vortx/shared-ui';
import ScreenshotCapture from './ScreenshotCapture';
import { useAuth } from '@vortx/shared-ui';
import { submitBugReport } from '../services/bugReportService';

const CATEGORIES = [
    { value: 'ui', label: 'UI / Display Issue' },
    { value: 'calculation', label: 'Calculation Error' },
    { value: 'performance', label: 'Performance / Slow' },
    { value: 'data', label: 'Data / Loading Issue' },
    { value: 'other', label: 'Other' }
];

export default function BugReportButton({ isDark }) {
    const { currentUser } = useAuth();
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';
    const [anchorEl, setAnchorEl] = useState(null);
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [hiddenForCapture, setHiddenForCapture] = useState(false);

    // Popover stays open during capture (but visually hidden) so ScreenshotCapture stays mounted
    const open = Boolean(anchorEl);

    const handleCaptureStart = () => {
        // Hide popover while capturing screenshot
        setHiddenForCapture(true);
    };

    const handleScreenshotChange = (data) => {
        setScreenshot(data);
        // Re-show popover after capture completes
        if (hiddenForCapture) {
            setHiddenForCapture(false);
        }
    };

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setError('');
    };

    const handleClose = () => {
        setAnchorEl(null);
        setCategory('');
        setDescription('');
        setScreenshot(null);
        setError('');
    };

    const handleSubmit = async () => {
        if (!category) {
            setError('Please select a category');
            return;
        }
        if (!description.trim()) {
            setError('Please describe the bug');
            return;
        }
        if (!screenshot) {
            setError('Please add a screenshot');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            await submitBugReport({
                category,
                description,
                screenshot
            }, currentUser);

            setSuccess(true);
            handleClose();
        } catch (err) {
            console.error('Failed to submit bug report:', err);
            setError(err.message || 'Failed to submit bug report. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Tooltip title="Report a Bug">
                <IconButton
                    onClick={handleOpen}
                    sx={{
                        color: isRetro ? vortxColors.gold : vortxColors.white,
                        mr: 1,
                        '&:hover': {
                            bgcolor: isRetro ? 'rgba(178, 140, 80, 0.2)' : 'rgba(255, 255, 255, 0.2)'
                        }
                    }}
                >
                    <BugReport />
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                disableEnforceFocus  // Allow focus even when Dialog is open
                disableAutoFocus     // Don't auto-focus, let user interact naturally
                sx={{
                    zIndex: 10000  // Above MUI Dialog (1300) so bug report form appears over modals
                }}
                PaperProps={{
                    sx: {
                        width: 400,
                        p: 2,
                        bgcolor: isRetro ? vortxColors.charcoal : vortxColors.white,
                        border: `1px solid ${isRetro ? vortxColors.gold : vortxColors.stone}`,
                        fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                        // Hide visually during capture but keep mounted so ScreenshotCapture can render overlay
                        visibility: hiddenForCapture ? 'hidden' : 'visible',
                        pointerEvents: hiddenForCapture ? 'none' : 'auto',
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ color: isRetro ? vortxColors.gold : vortxColors.charcoal }}>
                        Report a Bug
                    </Typography>
                    <IconButton size="small" onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ color: isRetro ? vortxColors.stone : undefined }}>Category *</InputLabel>
                    <Select
                        value={category}
                        label="Category *"
                        onChange={(e) => setCategory(e.target.value)}
                        sx={{
                            bgcolor: isRetro ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
                            color: isRetro ? vortxColors.white : vortxColors.charcoal,
                        }}
                        MenuProps={{
                            sx: { zIndex: 10001 }  // Above Popover and AppBar
                        }}
                    >
                        {CATEGORIES.map((cat) => (
                            <MenuItem key={cat.value} value={cat.value}>
                                {cat.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description *"
                    placeholder="Describe the bug. What did you expect to happen? What actually happened?"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiInputBase-root': {
                            bgcolor: isRetro ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
                            color: isRetro ? vortxColors.white : vortxColors.charcoal,
                        },
                        '& .MuiInputLabel-root': {
                            color: isRetro ? vortxColors.stone : vortxColors.charcoal,
                        },
                    }}
                />

                <ScreenshotCapture
                    onScreenshotChange={handleScreenshotChange}
                    onCaptureStart={handleCaptureStart}
                    isDark={isRetro}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={handleClose} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        sx={{
                            bgcolor: vortxColors.gold,
                            '&:hover': { bgcolor: vortxColors.darkGold || '#9a7a45' }
                        }}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Submit Bug'}
                    </Button>
                </Box>
            </Popover>

            <Snackbar
                open={success}
                autoHideDuration={4000}
                onClose={() => setSuccess(false)}
                message="Bug report submitted successfully!"
            />
        </>
    );
}
